// Parsing utilities for AI-generated CAB report JSON.
// Kept separate from App.js so they can be unit-tested directly.

/**
 * Character-level scanner that sanitizes literal control characters
 * inside JSON string values.  AI models occasionally write a raw newline
 * or tab instead of the escaped form, which produces "Bad control character
 * in string literal" on JSON.parse.  This pass is safe: it only rewrites
 * characters whose code-point is below 0x20 that are inside a JSON string
 * and are not already escaped.
 *
 * @param {string} str
 * @returns {string}
 */
function sanitizeJsonControlChars(str) {
  let out = '';
  let inString = false;
  let escaped  = false;
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (escaped)               { out += c; escaped = false; continue; }
    if (c === '\\' && inString){ out += c; escaped = true;  continue; }
    if (c === '"')             { inString = !inString; out += c; continue; }
    if (inString) {
      const code = c.charCodeAt(0);
      if      (c === '\n') { out += '\\n';  continue; }
      else if (c === '\r') { out += '\\r';  continue; }
      else if (c === '\t') { out += '\\t';  continue; }
      else if (code < 0x20) {
        out += '\\u' + code.toString(16).padStart(4, '0');
        continue;
      }
    }
    out += c;
  }
  return out;
}

/**
 * Character-level scanner that inserts missing commas between adjacent JSON
 * values outside of string context.  Two classes of missing comma are handled:
 *
 *   Class A: closing bracket/brace followed by a new value
 *     [{"a":1} {"b":2}]  becomes  [{"a":1},{"b":2}]
 *
 *   Class B: closing string quote followed by a new value (string arrays)
 *     ["JOHN DOE" "JANE SMITH"]  becomes  ["JOHN DOE","JANE SMITH"]
 *     ["Dry Van" {"extra":true}] becomes  ["Dry Van",{"extra":true}]
 *
 * For Class B, a comma is only inserted when the next non-whitespace character
 * after the closing quote is a value-starter (", {, [, digit, t, f, n).
 * If it is :, ,, ], or } the quote ended an object key or the comma already
 * exists and no action is taken.
 *
 * The scanner tracks string context to avoid acting on sequences that appear
 * inside string values.
 *
 * @param {string} str
 * @returns {string}
 */
function fixMissingCommas(str) {
  let out = '';
  let inString = false;
  let escaped  = false;
  let i = 0;
  while (i < str.length) {
    const c = str[i];
    if (escaped)               { out += c; escaped = false; i++; continue; }
    if (c === '\\' && inString){ out += c; escaped = true;  i++; continue; }

    // Class B: handle closing quotes on string values.
    if (c === '"') {
      const wasInString = inString;
      inString = !inString;
      out += c;
      i++;
      if (wasInString) {
        // We just closed a string -- peek past whitespace.
        const wsStart = i;
        while (i < str.length && /[ \t\r\n]/.test(str[i])) i++;
        const ws = str.slice(wsStart, i);
        if (i < str.length) {
          const next = str[i];
          // Value-starters after a closed string with no comma/colon = missing comma.
          if (next === '"' || next === '{' || next === '[' || /[0-9tfn]/.test(next)) {
            out += ws + ',';
          } else {
            out += ws;
          }
        } else {
          out += ws;
        }
      }
      continue;
    }

    // Class A: closing bracket/brace followed by a new object or array.
    if (!inString && (c === '}' || c === ']')) {
      out += c;
      i++;
      const wsStart = i;
      while (i < str.length && /[ \t\r\n]/.test(str[i])) i++;
      const ws = str.slice(wsStart, i);
      if (i < str.length && (str[i] === '{' || str[i] === '[')) {
        out += ws + ',';
      } else {
        out += ws;
      }
      continue;
    }

    out += c;
    i++;
  }
  return out;
}

/**
 * Closes a truncated JSON string by appending the missing closing tokens.
 *
 * When the AI model hits its token limit mid-response the JSON is cut off
 * before all arrays and objects are closed.  This function scans the string
 * with full string-context tracking, builds a stack of expected closing
 * tokens, and appends them in reverse order.
 *
 * If the string ends inside an open string literal, a closing quote is
 * appended first before the structural closing tokens.
 *
 * Examples:
 *   '{"a": [{"x": 1}'  ->  '{"a": [{"x": 1}]}'
 *   '["a", "b'         ->  '["a", "b"]'
 *
 * @param {string} str
 * @returns {string}
 */
function closeTruncatedJson(str) {
  let inString = false;
  let escaped  = false;
  const stack  = [];
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    if (escaped)               { escaped = false; continue; }
    if (c === '\\' && inString){ escaped = true;  continue; }
    if (c === '"')             { inString = !inString; continue; }
    if (!inString) {
      if      (c === '{') stack.push('}');
      else if (c === '[') stack.push(']');
      else if (c === '}' || c === ']') stack.pop();
    }
  }
  // Close an open string literal first, then close structural tokens.
  const suffix = (inString ? '"' : '') + stack.reverse().join('');
  return str + suffix;
}

/**
 * Returns true when the JSON.parse error position is at or past the end of
 * the string, which reliably indicates that the model response was truncated
 * (the parser consumed everything and still expected more tokens).
 *
 * @param {number} pos  - character position from extractErrorPosition
 * @param {number} len  - string length
 * @returns {boolean}
 */
function isTruncation(pos, len) {
  return pos >= len;
}

/**
 * Extracts the integer character position from the message of a SyntaxError
 * thrown by JSON.parse.  Node 20+ and modern V8 include it in the message.
 * Returns -1 if the position cannot be parsed.
 *
 * @param {Error} err
 * @returns {number}
 */
function extractErrorPosition(err) {
  const m = (err.message || '').match(/position (\d+)/);
  return m ? parseInt(m[1], 10) : -1;
}

/**
 * Parses an AI-generated CAB report JSON string robustly.
 *
 * Pipeline:
 *   1. Strip markdown code fences.
 *   2. Extract the outermost { ... } block.
 *   3. Sanitize literal control characters inside string values.
 *   4. Attempt JSON.parse directly.
 *   5. On failure: log a diagnostic window (+-250 chars) around the error
 *      position, then try a sequence of targeted repairs:
 *        a. fixMissingCommas -- handles adjacent values without commas
 *        b. closeTruncatedJson -- handles model response cut off at token limit
 *        c. fixMissingCommas(closeTruncatedJson(...)) -- both combined
 *      Return the first repair that produces valid JSON, logging a warning.
 *   6. If all repairs fail: log diagnostics again and throw a descriptive error.
 *
 * Diagnostics show the exact character window around the failing position.
 * No sensitive data (credentials, tokens) is logged.
 *
 * @param {string} raw   - Raw text returned by the AI model
 * @param {string} [label] - Short context label, e.g. 'CAB pass 1'
 * @returns {object} Parsed JSON object
 * @throws {Error} With position and context information if all repairs fail
 */
function parseCABJson(raw, label) {
  const ctx = label || 'CAB';

  // 1. Strip markdown fences
  let text = raw
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```\s*$/, '');

  // 2. Extract outermost { ... }
  const start = text.indexOf('{');
  const end   = text.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) {
    // Model may have been truncated before writing any closing brace.
    // Try closing truncation on the raw extracted text.
    const fromOpen = text.indexOf('{');
    if (fromOpen === -1) {
      throw new Error(
        `[${ctx}] No JSON object found in model response (response length: ${raw.length}). ` +
        `Starts with: ${raw.slice(0, 150)}`
      );
    }
    // Fall through with whatever we have from the opening brace onward.
    text = text.slice(fromOpen);
  } else {
    text = text.slice(start, end + 1);
  }

  // 3. Sanitize literal control characters inside strings
  const jsonStr = sanitizeJsonControlChars(text);

  // 4. First parse attempt
  try {
    return JSON.parse(jsonStr);
  } catch (firstErr) {
    const WINDOW = 250;
    const pos = extractErrorPosition(firstErr);
    logDiagnostics(ctx, 'First parse failed', firstErr, jsonStr, pos, WINDOW);
    const truncated = isTruncation(pos, jsonStr.length);

    // 5. Try repairs in order of specificity.
    const candidates = [
      { label: 'missing-comma repair',          fn: () => fixMissingCommas(jsonStr) },
      { label: 'truncation close',              fn: () => closeTruncatedJson(jsonStr),                    skip: !truncated },
      { label: 'truncation close + comma repair', fn: () => fixMissingCommas(closeTruncatedJson(jsonStr)), skip: !truncated },
    ];

    for (const { label: repairLabel, fn, skip } of candidates) {
      if (skip) continue;
      try {
        const result = JSON.parse(fn());
        console.warn(
          `[${ctx}] JSON parse recovered via "${repairLabel}". ` +
          (truncated ? 'Response was truncated at token limit -- some fields may be missing. ' : '') +
          `Original error: ${firstErr.message}`
        );
        return result;
      } catch (_) {
        // try next candidate
      }
    }

    // All repairs failed -- log final state and throw.
    const lastRepaired = fixMissingCommas(closeTruncatedJson(jsonStr));
    const pos2 = extractErrorPosition((() => { try { JSON.parse(lastRepaired); } catch(e) { return e; } return new Error(); })());
    logDiagnostics(ctx, 'All repairs failed', firstErr, lastRepaired, pos2 >= 0 ? pos2 : pos, WINDOW);

    const hint = truncated
      ? 'The model response was cut off at the token limit. The fields after the truncation point are missing.'
      : 'Comma repair did not resolve the issue.';

    throw new Error(
      `CAB report JSON could not be parsed (${firstErr.message} at position ${pos >= 0 ? pos : '?'}). ` +
      `${hint} Check the browser console for the exact location in the model response.`
    );
  }
}

function logDiagnostics(ctx, phase, err, jsonStr, pos, window) {
  const before = pos >= 0 ? jsonStr.slice(Math.max(0, pos - window), pos) : '';
  const after  = pos >= 0 ? jsonStr.slice(pos, pos + window) : '';
  console.error(
    `[${ctx}] ${phase}: ${err.message}\n` +
    `  Payload length: ${jsonStr.length} chars\n` +
    (pos >= 0
      ? `  Context before pos ${pos}:\n${JSON.stringify(before)}\n` +
        `  Context after  pos ${pos}:\n${JSON.stringify(after)}`
      : '  (position unknown -- runtime may not include position info)')
  );
}

const CAB_BASIC_KEYS = [
  'unsafeDriving', 'hoursOfService', 'driverFitness', 'controlledSubstances',
  'vehicleMaintenance', 'hazmat', 'crashIndicator',
];

/**
 * Normalizes an AI-generated CAB object so that every field React renders
 * as an array is actually an array, and every field rendered as an object is
 * actually an object.
 *
 * The AI occasionally returns a string where an array is expected (e.g.
 * `"alerts": "High ISS"` instead of `"alerts": ["High ISS"]`).  Calling
 * `cabData.alerts.map(...)` on a string throws `TypeError: .map is not a
 * function` during React rendering, which — without an error boundary —
 * produces a blank white screen.
 *
 * This function is a pure transformation: it never fabricates values, it
 * only coerces types.  Missing optional fields default to their empty form.
 *
 * @param {object} parsed — object returned by parseCABJson
 * @returns {object}      — same object with type-safe fields (mutated in place)
 */
function sanitizeCabData(parsed) {
  if (!parsed || typeof parsed !== 'object') return {};

  // String arrays: a bare string is a valid single element (e.g. one alert, one contact).
  const ensureStringArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim()) return [val];
    return [];
  };

  // Object arrays: elements are expected to be objects; a bare string means
  // the AI returned a summary instead of a list, so discard it.
  const ensureObjectArray = (val) => {
    if (Array.isArray(val)) return val;
    return [];
  };

  // String element arrays (wrapping a single string is safe/useful)
  parsed.alerts = ensureStringArray(parsed.alerts);
  parsed.cargo  = ensureStringArray(parsed.cargo);

  // company.contacts
  if (parsed.company && typeof parsed.company === 'object') {
    parsed.company.contacts = ensureStringArray(parsed.company.contacts);
  }

  // fleet — history and radiusDistribution contain objects, not plain strings
  if (parsed.fleet && typeof parsed.fleet === 'object') {
    parsed.fleet.history            = ensureObjectArray(parsed.fleet.history);
    parsed.fleet.radiusDistribution = ensureObjectArray(parsed.fleet.radiusDistribution);
  }

  // insurance.history
  if (parsed.insurance && typeof parsed.insurance === 'object') {
    parsed.insurance.history = ensureObjectArray(parsed.insurance.history);
  }

  // accidents.details
  if (parsed.accidents && typeof parsed.accidents === 'object') {
    parsed.accidents.details = ensureObjectArray(parsed.accidents.details);
  }

  // violations — entries are objects, not plain strings
  if (parsed.violations && typeof parsed.violations === 'object') {
    parsed.violations.vehicle = ensureObjectArray(parsed.violations.vehicle);
    parsed.violations.driver  = ensureObjectArray(parsed.violations.driver);
    parsed.violations.hazmat  = ensureObjectArray(parsed.violations.hazmat);
  }

  // inspections.annual
  if (parsed.inspections && typeof parsed.inspections === 'object') {
    parsed.inspections.annual = ensureObjectArray(parsed.inspections.annual);
  }

  // basics — each key must be an object with score and alert
  if (parsed.basics && typeof parsed.basics === 'object') {
    for (const key of CAB_BASIC_KEYS) {
      const b = parsed.basics[key];
      if (!b || typeof b !== 'object' || Array.isArray(b)) {
        parsed.basics[key] = { score: null, alert: false };
      }
    }
  }

  return parsed;
}

/**
 * Validates that expected array/object fields in an AI-generated or persisted
 * CAB record have the correct types, logging a warning for each mismatch.
 *
 * Run this BEFORE sanitizeCabData so warnings describe the original bad value,
 * not the already-fixed one.
 *
 * Safety rules:
 *  - Never logs actual field values — only the JavaScript typeof.
 *  - Never logs alerts text, driver names, or other CAB content.
 *  - ctx identifies the call site (e.g. 'AI response', 'Redis load').
 *
 * @param {object} parsed
 * @param {string} ctx
 */
function validateCabSchema(parsed, ctx) {
  if (!parsed || typeof parsed !== 'object') return;

  const warn = (field, expected, actual) =>
    console.warn('[CAB schema]', ctx, '—', field, ': expected', expected, ', got', actual);

  const checkArray = (val, field) => {
    if (val !== undefined && !Array.isArray(val)) warn(field, 'array', typeof val);
  };
  const checkObject = (val, field) => {
    if (val !== undefined && (typeof val !== 'object' || val === null || Array.isArray(val)))
      warn(field, 'object', Array.isArray(val) ? 'array' : typeof val);
  };

  checkArray(parsed.alerts, 'alerts');
  checkArray(parsed.cargo,  'cargo');

  if (parsed.company && typeof parsed.company === 'object') {
    checkArray(parsed.company.contacts, 'company.contacts');
  }
  if (parsed.fleet && typeof parsed.fleet === 'object') {
    checkArray(parsed.fleet.history,            'fleet.history');
    checkArray(parsed.fleet.radiusDistribution, 'fleet.radiusDistribution');
  }
  if (parsed.insurance && typeof parsed.insurance === 'object') {
    checkArray(parsed.insurance.history, 'insurance.history');
  }
  if (parsed.accidents && typeof parsed.accidents === 'object') {
    checkArray(parsed.accidents.details, 'accidents.details');
  }
  if (parsed.violations && typeof parsed.violations === 'object') {
    checkArray(parsed.violations.vehicle, 'violations.vehicle');
    checkArray(parsed.violations.driver,  'violations.driver');
    checkArray(parsed.violations.hazmat,  'violations.hazmat');
  }
  if (parsed.inspections && typeof parsed.inspections === 'object') {
    checkArray(parsed.inspections.annual, 'inspections.annual');
  }
  if (parsed.basics && typeof parsed.basics === 'object') {
    for (const key of CAB_BASIC_KEYS) {
      checkObject(parsed.basics[key], `basics.${key}`);
    }
  }
}

module.exports = { parseCABJson, sanitizeCabData, validateCabSchema, sanitizeJsonControlChars, fixMissingCommas, closeTruncatedJson };
